import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

const MEDIA = 'https://media.easy-peasy.ai/landing/home';
const ASSETS = 'https://media.easy-peasy.ai/assets';

export type ShowcaseItem = {
  src: string;
  poster?: string;
  video?: boolean;
  // width relative to the fixed row height
  aspect: string;
  label: string;
  href: string;
};

const ROW_A: ShowcaseItem[] = [
  {
    src: `${MEDIA}/vid-coast.mp4`,
    poster: `${MEDIA}/vid-coast-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Cinematic video',
    href: '/ai-video-generator',
  },
  {
    src: `${ASSETS}/news-anchor.mp4`,
    poster: `${MEDIA}/news-anchor-poster.jpg`,
    video: true,
    aspect: '1/1',
    label: 'Talking avatars',
    href: '/ai-video-generator/talking',
  },
  {
    src: `${MEDIA}/img-perfume.webp`,
    aspect: '1/1',
    label: 'Product shots',
    href: '/ai-image-generator',
  },
  {
    src: 'https://media.easy-peasy.ai/landing/seedance-2-5/official-camera-moves.mp4',
    poster:
      'https://media.easy-peasy.ai/landing/seedance-2-5/official-camera-moves-poster.jpg',
    video: true,
    aspect: '16/9',
    label: 'Seedance 2.5',
    href: '/ai-video-generator/seedance-2-5',
  },
  {
    src: `${MEDIA}/vid-oldpc.mp4`,
    poster: `${MEDIA}/vid-oldpc-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Websites',
    href: '/marky',
  },
  {
    src: `${ASSETS}/stadium-kiss-800.mp4`,
    poster: `${MEDIA}/stadium-kiss-800-poster.jpg`,
    video: true,
    aspect: '3/4',
    label: 'Video templates',
    href: '/templates',
  },
  {
    src: `${MEDIA}/img-anime.webp`,
    aspect: '3/4',
    label: 'Anime art',
    href: '/ai-image-generator',
  },
  {
    src: `${MEDIA}/vid-slides.mp4`,
    poster: `${MEDIA}/vid-slides-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Slide decks',
    href: '/marky',
  },
  {
    src: `${MEDIA}/vid-butterfly.mp4`,
    poster: `${MEDIA}/vid-butterfly-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Nature film',
    href: '/ai-video-generator',
  },
  {
    src: `${MEDIA}/img-castle.webp`,
    aspect: '9/16',
    label: 'Fantasy worlds',
    href: '/ai-image-generator',
  },
  {
    src: `${ASSETS}/motion-control-800-v2.mp4`,
    poster: `${MEDIA}/motion-control-800-v2-poster.jpg`,
    video: true,
    aspect: '3/4',
    label: 'Motion control',
    href: '/templates',
  },
];

const ROW_B: ShowcaseItem[] = [
  {
    src: `${MEDIA}/img-fashion.webp`,
    aspect: '3/4',
    label: 'Editorial fashion',
    href: '/ai-image-generator',
  },
  {
    src: `${MEDIA}/vid-neoncity.mp4`,
    poster: `${MEDIA}/vid-neoncity-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Night scenes',
    href: '/ai-video-generator',
  },
  {
    src: `${MEDIA}/img-headshot.webp`,
    aspect: '1/1',
    label: 'AI headshots',
    href: '/ai-photo-generator',
  },
  {
    src: `${ASSETS}/talking-vegetables-800.mp4`,
    poster: `${MEDIA}/talking-vegetables-800-poster.jpg`,
    video: true,
    aspect: '3/4',
    label: 'Character video',
    href: '/templates',
  },
  {
    src: `${MEDIA}/vid-music.mp4`,
    poster: `${MEDIA}/vid-music-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Music & songs',
    href: '/ai-music-generator',
  },
  {
    src: `${MEDIA}/img-astronaut.webp`,
    aspect: '16/9',
    label: 'Concept art',
    href: '/ai-image-generator',
  },
  {
    src: `${MEDIA}/vid-perfume.mp4`,
    poster: `${MEDIA}/vid-perfume-poster.jpg`,
    video: true,
    aspect: '16/9',
    label: 'Product ads',
    href: '/ai-video-generator',
  },
  {
    src: `${MEDIA}/audio-tts.webp`,
    aspect: '4/3',
    label: 'Voiceovers',
    href: '/text-to-speech',
  },
  {
    src: `${MEDIA}/img-robot.webp`,
    aspect: '1/1',
    label: '3D characters',
    href: '/ai-image-generator',
  },
  {
    src: `${MEDIA}/img-interior.webp`,
    aspect: '4/3',
    label: 'Interior design',
    href: '/ai-interior-design-generator',
  },
];

const ShowcaseCard = ({
  item,
  ariaHidden,
}: {
  item: ShowcaseItem;
  ariaHidden?: boolean;
}) => {
  const { t } = useLocale('home');

  return (
    <Link
      href={item.href}
      tabIndex={ariaHidden ? -1 : undefined}
      aria-hidden={ariaHidden || undefined}
      className='group relative h-56 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition-transform duration-300 hover:scale-[1.02] md:h-72'
      style={{ aspectRatio: item.aspect }}
    >
      {item.video ? (
        <AutoPlayVideo
          src={item.src}
          poster={item.poster}
          className='absolute inset-0 h-full w-full object-cover'
        />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={item.src}
          alt={item.label}
          loading='lazy'
          decoding='async'
          className='absolute inset-0 h-full w-full object-cover'
        />
      )}
      <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80' />
      <span className='absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md'>
        {t(item.label)}
      </span>
      <span
        aria-hidden='true'
        className='absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-md transition-opacity group-hover:opacity-100'
      >
        →
      </span>
    </Link>
  );
};

// A marquee the user can also drive: the row is a real horizontal scroller
// (touch swipe on mobile, trackpad pan, and mouse drag-to-scroll on desktop)
// with the auto-advance done in JS on scrollLeft instead of a CSS transform.
// The content is duplicated, so wrapping scrollLeft around the halfway point
// keeps both the animation and manual scrolling seamless in either direction.
// Auto-advance pauses while the pointer is over the row (the old CSS hover
// behaviour), while touching, and briefly after any manual scroll; it never
// runs under prefers-reduced-motion, where the row is still hand-scrollable.
export const MarqueeRow = ({
  items,
  duration,
  reverse,
}: {
  items: ShowcaseItem[];
  duration: number;
  reverse?: boolean;
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // True between a drag past the threshold and the click it would fire —
  // the capture-phase click handler swallows that click so dragging across
  // a card never navigates.
  const draggedRef = useRef(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const half = () => el.scrollWidth / 2;
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    let paused = false;
    let dragging = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const pause = () => {
      paused = true;
      if (idleTimer) clearTimeout(idleTimer);
    };
    const resumeSoon = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        paused = false;
      }, 1600);
    };

    // scrollLeft rounds on read in some browsers, so slow speeds stall if we
    // accumulate on the element itself — keep our own float position and
    // adopt the element's value whenever the user scrolled it manually.
    let pos = reverse ? half() : 0;
    el.scrollLeft = pos;
    let last = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.4);
      last = now;
      if (Math.abs(el.scrollLeft - pos) > 1.5) pos = el.scrollLeft;
      if (!paused && !dragging && !reduced) {
        pos += ((reverse ? -1 : 1) * half() * dt) / duration;
      }
      const h = half();
      if (h > 0) {
        if (pos >= h) pos -= h;
        else if (pos <= 0) pos += h;
      }
      el.scrollLeft = pos;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    // Mouse drag-to-scroll. Touch is left to the native scroller.
    let dragStartX = 0;
    let dragStartPos = 0;
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true;
      draggedRef.current = false;
      dragStartX = e.clientX;
      dragStartPos = el.scrollLeft;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || e.pointerType !== 'mouse') return;
      const dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 6) draggedRef.current = true;
      if (draggedRef.current) {
        pos = dragStartPos - dx;
        el.scrollLeft = pos;
      }
    };
    const endDrag = () => {
      dragging = false;
      resumeSoon();
    };

    const onWheel = () => {
      pause();
      resumeSoon();
    };
    const onTouchStart = () => pause();

    el.addEventListener('pointerenter', pause);
    el.addEventListener('pointerleave', resumeSoon);
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', resumeSoon);
    el.addEventListener('wheel', onWheel, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      if (idleTimer) clearTimeout(idleTimer);
      el.removeEventListener('pointerenter', pause);
      el.removeEventListener('pointerleave', resumeSoon);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', resumeSoon);
      el.removeEventListener('wheel', onWheel);
    };
  }, [duration, reverse]);

  return (
    <div
      ref={scrollerRef}
      className='showcase-row relative cursor-grab overflow-x-auto overscroll-x-contain select-none active:cursor-grabbing'
      onClickCapture={(e) => {
        if (draggedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          draggedRef.current = false;
        }
      }}
      onDragStart={(e) => e.preventDefault()}
    >
      <div className='flex w-max gap-4 pr-4'>
        {[0, 1].map((copy) => (
          <div key={copy} className='flex gap-4'>
            {items.map((item) => (
              <ShowcaseCard
                key={`${copy}-${item.src}`}
                item={item}
                ariaHidden={copy === 1}
              />
            ))}
          </div>
        ))}
      </div>
      {/* Scrollbar is hidden — the row still scrolls by touch, trackpad and
          drag; styled-jsx dedupes identical global blocks across consumers. */}
      <style jsx global>{`
        .showcase-row {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .showcase-row::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

const ShowcaseMarquee = () => {
  const { t } = useLocale('home');

  return (
    <section id='showcase' className='relative overflow-hidden py-20 lg:py-28'>
      <div className='mx-auto mb-12 flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-end lg:px-8'>
        <div>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('Made with Easy-Peasy')}
          </h2>
          <p className='mt-4 max-w-xl text-lg text-slate-400'>
            {t(
              'Real results from the tools on this page — images, videos, avatars and more. No skills required.',
            )}
          </p>
        </div>
        <Link
          href='/templates'
          className='shrink-0 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/10'
        >
          {t('Explore all templates')} →
        </Link>
      </div>

      <div className='flex flex-col gap-4'>
        <MarqueeRow items={ROW_A} duration={100} />
        <MarqueeRow items={ROW_B} duration={115} reverse />
      </div>
    </section>
  );
};

export default ShowcaseMarquee;
